import { BASE_CHAIN_ID } from "@breakout/shared"
import { useFundWallet } from "@privy-io/react-auth"
import { useEffect, useRef } from "react"
import type { Address } from "viem"
import { breakoutChain } from "../../lib/providers"

const FUND_WALLET_SHOWN_KEY = "privy_fundwallet_shown_v1"

interface UseAutoFundOnFirstLoginParams {
	ready: boolean
	isAuthenticated: boolean
	embeddedWalletAddress: Address | null
	chainId: number
}

/**
 * Auto-opens the Privy fund wallet modal on first successful login.
 * Only triggers once per browser (persisted via localStorage).
 */
export function useAutoFundOnFirstLogin({
	ready,
	isAuthenticated,
	embeddedWalletAddress,
	chainId,
}: UseAutoFundOnFirstLoginParams): void {
	const { fundWallet } = useFundWallet()
	// Guard against React StrictMode double-invocation
	const hasTriggeredRef = useRef(false)

	useEffect(() => {
		// All conditions must be met:
		// 1. Privy is ready
		// 2. User is authenticated
		// 3. Embedded wallet address exists
		// 4. On mainnet (Privy onramps don't work on testnets)
		// 5. Haven't shown yet (localStorage)
		// 6. Haven't triggered this effect yet (ref guard for StrictMode)
		if (!ready || !isAuthenticated || !embeddedWalletAddress) {
			return
		}

		// Skip auto-fund on testnet - Privy onramps only work on mainnet
		if (chainId !== BASE_CHAIN_ID) {
			return
		}

		// Check localStorage
		try {
			if (localStorage.getItem(FUND_WALLET_SHOWN_KEY) === "true") {
				return
			}
		} catch {
			// localStorage not available, skip auto-open
			return
		}

		// Ref guard for StrictMode double-invocation
		if (hasTriggeredRef.current) {
			return
		}
		hasTriggeredRef.current = true

		// Set flag immediately (before async operation)
		try {
			localStorage.setItem(FUND_WALLET_SHOWN_KEY, "true")
		} catch {
			// Ignore localStorage errors
		}

		// Open the fund wallet modal
		// User closing modal is fine - flag is already set
		fundWallet({
			address: embeddedWalletAddress,
			options: { chain: breakoutChain }, // Explicitly fund on Base
		}).catch(error => {
			// Log errors for debugging (user may have closed modal, which is fine)
			console.warn("[FundWallet] Auto-fund modal error:", error)
		})
	}, [ready, isAuthenticated, embeddedWalletAddress, chainId, fundWallet])
}
